import { useEffect, useMemo, useRef, useState } from 'react';

import { bindAll } from 'bind-event-listener';

import { type ConversationChannel } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { AssistanceServiceImpl } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { useAISmartButtonContext } from '../../context';
import { isBrowser } from '../../utils/is-browser';
import { schedulePostPaint } from '../../utils/schedule-post-paint';

const LOOK_BACK_PERIOD_MS = 36 * 60 * 60 * 1000;
const CACHE_TTL_MS = 5 * 60 * 1000;
const CACHE_KEY = 'rovo-button-unread-status-v1';

type UnreadConversationSummary =
	| {
			hasUnread: true;
			inProgressCount: number;
			mostRecentUnread: {
				id: string;
				name: string | null;
			};
			unreadCount: number;
	  }
	| {
			hasUnread: false;
			inProgressCount: number;
			mostRecentUnread: null;
			unreadCount: 0;
	  };

type CachedResult = {
	cachedAt: number;
	hasUnread: boolean;
	inProgressCount: number;
	mostRecentUnread: { id: string; name: string | null } | null;
	stale?: boolean;
	unreadCount: number;
};

function readCache(): { data: UnreadConversationSummary; isStale: boolean } | null {
	if (!isBrowser()) {
		return null;
	}
	try {
		const raw = sessionStorage.getItem(CACHE_KEY);
		if (!raw) {
			return null;
		}
		const parsed: CachedResult = JSON.parse(raw);
		// Entries older than TTL are fully expired — discard them
		if (Date.now() - parsed.cachedAt >= CACHE_TTL_MS) {
			sessionStorage.removeItem(CACHE_KEY);
			return null;
		}
		const { cachedAt: _cachedAt, stale, ...result } = parsed;
		// Migrate cache entries written before unreadCount / inProgressCount were added
		const data: UnreadConversationSummary = {
			...result,
			unreadCount: result.unreadCount ?? (result.hasUnread ? 1 : 0),
			inProgressCount: result.inProgressCount ?? 0,
		} as UnreadConversationSummary;
		return { data, isStale: stale === true };
	} catch {
		return null;
	}
}

function writeCache(result: UnreadConversationSummary): void {
	if (!isBrowser()) {
		return;
	}
	try {
		const toStore: CachedResult = { ...result, cachedAt: Date.now() };
		sessionStorage.setItem(CACHE_KEY, JSON.stringify(toStore));
	} catch {
		// sessionStorage may be unavailable (private browsing quota, etc.) — fail silently
	}
}

/**
 * Marks the cached unread conversation status as stale without removing it.
 * The cached value continues to be displayed while a background re-fetch
 * updates the indicator — stale-while-revalidate.
 *
 * Call this directly when the user closes the mini-modal or sidebar. After marking
 * stale, the next call to `fetchAndUpdate` (triggered by post-paint scheduling or
 * window focus) will show the cached value immediately while re-fetching in the
 * background, then update the indicator once the fresh result arrives.
 */
export function markUnreadCacheStale(): void {
	if (!isBrowser()) {
		return;
	}
	try {
		const raw = sessionStorage.getItem(CACHE_KEY);
		if (!raw) {
			return;
		}
		const parsed: CachedResult = JSON.parse(raw);
		sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ...parsed, stale: true }));
	} catch {
		// sessionStorage may be unavailable — fail silently
	}
}

const NO_UNREAD: UnreadConversationSummary = {
	hasUnread: false,
	unreadCount: 0,
	inProgressCount: 0,
	mostRecentUnread: null,
};

/**
 * Fetches conversation channel data after the page has painted (post-vc90) and
 * derives whether the user has any unread conversations from the past 36 hours.
 *
 * Results are cached in sessionStorage with a 5-minute TTL and a stale-while-revalidate
 * strategy: when the cache is marked stale (e.g. after the user closes the mini-modal or
 * sidebar), the cached value is shown immediately while a background re-fetch updates the
 * indicator without causing a flash.
 *
 * On any fetch failure, the currently displayed status is preserved rather than resetting
 * to no-indicator — a false negative is worse UX than a briefly stale indicator.
 */
export function useUnreadConversationStatus({
	enabled,
	mode,
}: {
	enabled: boolean;
	mode?: string;
}): UnreadConversationSummary {
	const [status, setStatus] = useState<UnreadConversationSummary>(() =>
		enabled ? (readCache()?.data ?? NO_UNREAD) : NO_UNREAD,
	);
	const { conversationAssistantConfig, product } = useAISmartButtonContext();
	const baseUrl = conversationAssistantConfig?.assistanceServiceConfig?.baseUrl;
	const headers = conversationAssistantConfig?.assistanceServiceConfig?.headers;
	const assistanceService = useMemo(
		() =>
			new AssistanceServiceImpl({
				product,
				experienceId: 'ai-mate',
				baseUrl,
				headers,
			}),
		[product, baseUrl, headers],
	);

	const fetchAndUpdateRef = useRef<() => Promise<void>>(async () => {});
	const isFetchingRef = useRef(false);
	const prevModeRef = useRef<string | undefined>(mode);

	useEffect(() => {
		if (!enabled) {
			// Reset the in-flight guard so the next enable can fetch immediately.
			// Without this, a fetch that was in-flight when enabled flipped to false
			// would leave isFetchingRef stuck as true, blocking the next fetch.
			isFetchingRef.current = false;
			setStatus(NO_UNREAD);
			return;
		}

		let cancelled = false;

		const fetchAndUpdate = async () => {
			if (isFetchingRef.current) {
				return;
			}
			isFetchingRef.current = true;
			try {
				const cacheResult = readCache();
				if (cacheResult) {
					if (!cancelled) {
						setStatus((prev) => {
							if (
								prev.hasUnread === cacheResult.data.hasUnread &&
								prev.unreadCount === cacheResult.data.unreadCount &&
								prev.inProgressCount === cacheResult.data.inProgressCount
							) {
								return prev;
							}
							return cacheResult.data;
						});
					}
					if (!cacheResult.isStale) {
						return;
					}
					// stale — fall through to background re-fetch
				}

				const channels: ConversationChannel[] = await assistanceService.getConversationChannels();
				if (cancelled) {
					return;
				}

				const inProgressCount = channels.filter(
					(channel) => channel.last_agent_message_status === 'IN_PROGRESS',
				).length;

				const now = Date.now();
				const recentUnread = channels
					.filter((channel) => {
						// readStatus === false is the explicit unread signal; null means a legacy/old
						// conversation with no read-tracking — exclude it to avoid false positives.
						const isUnread = channel.readStatus === false;
						// Only show indicator for completed agent responses — exclude IN_PROGRESS (streaming),
						// FAILED, CANCELLED, and legacy channels without this field.
						const hasSuccessfulAgentMessage = channel.last_agent_message_status === 'SUCCESSFUL';
						const timestamp = channel.last_message_timestamp;
						const isRecent =
							!!timestamp && now - new Date(timestamp).getTime() < LOOK_BACK_PERIOD_MS;
						return isUnread && hasSuccessfulAgentMessage && isRecent;
					})
					.sort((a, b) => {
						const aTime = a.last_message_timestamp
							? new Date(a.last_message_timestamp).getTime()
							: 0;
						const bTime = b.last_message_timestamp
							? new Date(b.last_message_timestamp).getTime()
							: 0;
						return bTime - aTime;
					});

				const result: UnreadConversationSummary =
					recentUnread.length > 0
						? {
								hasUnread: true,
								unreadCount: recentUnread.length,
								inProgressCount,
								mostRecentUnread: {
									id: String(recentUnread[0].id),
									name: recentUnread[0].name ?? null,
								},
							}
						: {
								hasUnread: false,
								unreadCount: 0,
								inProgressCount,
								mostRecentUnread: null,
							};

				writeCache(result);
				setStatus(result);
			} catch {
				// Fail silently — keep the current displayed status rather than forcing NO_UNREAD.
				// If stale data was being shown, it continues to show — better UX than a false negative.
			} finally {
				isFetchingRef.current = false;
			}
		};

		fetchAndUpdateRef.current = fetchAndUpdate;

		// Defer the initial fetch until after paint/idle (post-vc90 safe)
		const cancelPostPaint = schedulePostPaint(() => {
			void fetchAndUpdate();
		});

		// Refresh on window focus once the cache has expired
		const unbindFocus = isBrowser()
			? bindAll(window, [
					{
						type: 'focus',
						listener: () => {
							void fetchAndUpdate();
						},
					},
				])
			: null;

		return () => {
			cancelled = true;
			cancelPostPaint();
			unbindFocus?.();
		};
	}, [assistanceService, enabled]);

	useEffect(() => {
		if (!enabled) {
			prevModeRef.current = mode;
			return;
		}
		if (mode === undefined && prevModeRef.current !== undefined) {
			// User closed the sidebar — mark cache stale and re-fetch in the background
			markUnreadCacheStale();
			void fetchAndUpdateRef.current();
		}
		prevModeRef.current = mode;
	}, [mode, enabled]);

	return status;
}
